<template>
  <panel-layout
    id="pass-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #actions-right>
      <div v-if="pass && !pass.isNewRecord" class="flex items-center space-x-2">
        <o-history-link :item="pass" model="Pass" icon="history" slim flat />
      </div>
    </template>

    <template #heading>
      <div v-if="pass" class="flex items-center space-x-2">
        <div>
          <h2
            class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100"
          >
            {{ pass.isNewRecord ? $t('pass.new') : pass.name }}
          </h2>
          <div v-if="!pass.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200">
              <span class="text-gray-700 dark:text-gray-200">{{ pass.infoLabel }}</span>
            </p>
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="pass" ref="form" class="h-full w-full">
      <o-tabs ref="tabsRef" nav-class="bg-gray-50 h-11 border-b px-2 dark:bg-gray-900" scroll>
        <o-tab :name="$t('general.details')" icon="details">
          <pass-details
            v-model:pass="pass"
            @updateImageSuccess="handleUpdateImageSuccess"
          />
        </o-tab>
        <o-tab :name="$t('general.services')" icon="services">
          <pass-services v-model:pass="pass" />
        </o-tab>
        <o-tab :name="$t('general.taxes')" icon="tax">
          <pass-taxes v-model:pass="pass" />
        </o-tab>
      </o-tabs>
    </o-form>

    <template #footer>
      <div v-if="pass">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button
              variant="primary"
              outline
              icon="save"
              class="mr-2"
              @click="save()"
            >
              {{ $t('general.save') }}
            </o-button>
            <o-button
              variant="primary"
              icon="save"
              class="mr-2"
              @click="save(true)"
            >
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <template v-if="pass">
      <deleteDialog ref="deleteDialog" @success="cancel(true)" />
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      icon="pass"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        {
          text: $t('general.closeWithoutSaving'),
          type: 'confirm',
          variant: undefined,
          flat: true,
          outline: true,
          callback: () => cancel(true)
        },
        {
          text: $t('general.saveAndClose'),
          type: 'confirm',
          response: 'save',
          callback: () => save(true)
        }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
import PassDetails from '@/components/pass/edit/Details.vue'
import PassServices from '@/components/pass/edit/Services.vue'
import PassTaxes from '@/components/pass/edit/Taxes.vue'

// @ts-ignore
import DeleteDialog from '@/components/pass/actions/Delete.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { OTabs, OTab, OAlert, OAvatar } from '@/components/elements'

import { PassExpiryMode } from '~/_api/models/pass'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()
const router = useRouter()

const pass = ref<any>(null)
const errors = ref(false)
const loading = ref(true)

const confirmDialog = ref<any>()
const deleteDialog = ref<any>()
const form = ref<any>()
const tabsRef = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(pass)

const fetchPass = async (id?: string) => {
  try {
    if (!id) {
      pass.value = (await $api.pass.defaults({} as any)).data

      pass.value.expires = true
      pass.value.expiryDays = 365
      pass.value.expiryMode = PassExpiryMode.booking
      pass.value.daysRemainingReminder = 7

      pass.value.isLimitedUse = true
      pass.value.numberOfUses = 3
      pass.value.usesRemainingReminder = 1
    } else {
      pass.value = (await $api.pass.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
}

const save = async (close = false) => {
  if (!await validateForm()) return false

  loading.value = true
  errors.value = false

  try {
    const isNewRecord = pass.value.isNewRecord
    const _pass = (await $api.pass.save(pass.value)).data

    if (_pass) {
      pass.value = _pass
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.pass.edit.success'),
        text: $i18n.t('pages.pass.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Pass', _pass)

      if (close) {
        panel.close()
      } else if (isNewRecord) {
        // Swap in-place to the freshly-created pass's edit view.
        panel.replace('pass/edit', { id: String(_pass.idPass) })
      }
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
    loading.value = false
    return false
  }

  loading.value = false
}

const validateForm = async () => {
  const previousTab = tabsRef.value?.getActiveTab()?.hash

  tabsRef.value?.selectTabByIndex(0)
  await nextTick()

  const valid = await form.value?.validate()

  if (valid && previousTab) {
    tabsRef.value?.selectTab(previousTab)
  }

  return Boolean(valid)
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleDelete = () => {
  deleteDialog.value.open(pass.value)
}

const handleUpdateImageSuccess = () => {
  fetchPass()
  $bus.emit.updated('Pass')
}

watch(() => props.id, async (id) => {
  loading.value = true
  await fetchPass(id)
  loading.value = false
}, { immediate: true })
</script>
